import { graphApi } from "@services/api/graphApi";
import { portalApi } from "@services/api/portalApi";
import type { AxiosResponse } from "axios";
import type {
	Channel,
	AadUserConversationMember,
	Group,
	Team,
} from "@microsoft/microsoft-graph-types";

type GraphCollectionResponse<T> = {
	value?: T[];
	"@odata.nextLink"?: string;
};

const graphV1BaseUrl = "https://graph.microsoft.com/v1.0";

function proxyPath(url: string): string {
	return url.startsWith(graphV1BaseUrl)
		? url.slice(graphV1BaseUrl.length)
		: url;
}

async function getAllPages<T>(initialUrl: string): Promise<T[]> {
	const values: T[] = [];
	let nextUrl: string | undefined = initialUrl;

	while (nextUrl) {
		const response: AxiosResponse<GraphCollectionResponse<T>> =
			await graphApi.get(proxyPath(nextUrl));
		values.push(...(response.data.value ?? []));
		nextUrl = response.data["@odata.nextLink"];
	}

	return values;
}

export async function listTeams(): Promise<TeamListItem[]> {
	try {
		return await getAllPages<TeamListItem>(
			"/groups?$filter=resourceProvisioningOptions/Any(x:x eq 'Team')&$select=id,displayName,description,visibility,createdDateTime&$top=999",
		);
	} catch (error) {
		console.error("Error fetching teams:", error);
		throw error;
	}
}

export async function getTeam(teamId: string): Promise<Team> {
	try {
		const response: AxiosResponse<Team> = await graphApi.get(
			`/teams/${teamId}`,
		);
		return response.data;
	} catch (error) {
		console.error(`Error fetching team with ID ${teamId}:`, error);
		throw error;
	}
}

export async function getGroup(groupId: string): Promise<Group> {
	try {
		const response: AxiosResponse<Group> = await graphApi.get(
			`/groups/${groupId}`,
		);
		return response.data;
	} catch (error) {
		console.error(`Error fetching group with ID ${groupId}:`, error);
		throw error;
	}
}

export async function getChannels(teamId: string): Promise<Channel[]> {
	try {
		return await getAllPages<Channel>(`/teams/${teamId}/allChannels`);
	} catch (error) {
		console.error(`Error fetching channels for team with ID ${teamId}:`, error);
		throw error;
	}
}

export async function getChannel(
	teamId: string,
	channelId: string,
): Promise<Channel> {
	try {
		const response: AxiosResponse<Channel> = await graphApi.get(
			`/teams/${teamId}/channels/${channelId}`,
		);
		return response.data;
	} catch (error) {
		console.error(
			`Error fetching channel with ID ${channelId} for team with ID ${teamId}:`,
			error,
		);
		throw error;
	}
}

export async function getTeamMembers(
	teamId: string,
): Promise<AadUserConversationMember[]> {
	try {
		return await getAllPages<AadUserConversationMember>(
			`/teams/${teamId}/members`,
		);
	} catch (error) {
		console.error(`Error fetching members for team with ID ${teamId}:`, error);
		throw error;
	}
}

export type EmptyTeamResult = {
	attempted: number;
	removed: number;
	failed: number;
};

export type TeamListItem = Pick<
	Group,
	"id" | "displayName" | "description" | "visibility" | "createdDateTime"
>;

export type TeamRemovalMode =
	| "empty"
	| "membersOnly"
	| "students"
	| "inactiveStudents"
	| "l6Students"
	| "u6Students";

export type TeamRemovalPreviewMember = {
	membershipId: string;
	userId?: string;
	displayName?: string;
	email?: string;
	isOwner: boolean;
};

export async function previewTeamRemoval(
	teamId: string,
	mode: TeamRemovalMode,
): Promise<TeamRemovalPreviewMember[]> {
	const response = await portalApi.post<TeamRemovalPreviewMember[]>(
		`/teams-administration/teams/${encodeURIComponent(teamId)}/removals/preview`,
		{ mode },
	);
	return response.data;
}

export async function executeTeamRemoval(
	teamId: string,
	mode: TeamRemovalMode,
): Promise<EmptyTeamResult> {
	const response = await portalApi.post<EmptyTeamResult>(
		`/teams-administration/teams/${encodeURIComponent(teamId)}/removals`,
		{ mode },
	);
	return response.data;
}

export type KnownTeamType = { typeId: number; name: string };
export type KnownTeam = { teamId: string; typeId: number };

export async function getKnownTeamTypes(): Promise<KnownTeamType[]> {
	return (await portalApi.get<KnownTeamType[]>("/teams-administration/types"))
		.data;
}

export async function getKnownTeams(): Promise<KnownTeam[]> {
	return (await portalApi.get<KnownTeam[]>("/teams-administration/known-teams"))
		.data;
}

export async function setKnownTeamType(
	teamId: string,
	typeId: number | null,
): Promise<void> {
	await portalApi.put(
		`/teams-administration/teams/${encodeURIComponent(teamId)}/type`,
		{ typeId },
	);
}

export async function executeMultiTeamRemoval(
	teamIds: string[],
	mode: TeamRemovalMode,
): Promise<EmptyTeamResult> {
	return (
		await portalApi.post<EmptyTeamResult>(
			"/teams-administration/teams/removals",
			{ teamIds, mode },
		)
	).data;
}

export async function emptyTeam(
	teamId: string,
	membersOnly: boolean,
): Promise<EmptyTeamResult> {
	return executeTeamRemoval(teamId, membersOnly ? "membersOnly" : "empty");
}

export async function getChannelMembers(
	teamId: string,
	channelId: string,
): Promise<AadUserConversationMember[]> {
	try {
		return await getAllPages<AadUserConversationMember>(
			`/teams/${teamId}/channels/${channelId}/members`,
		);
	} catch (error) {
		console.error(
			`Error fetching members for channel with ID ${channelId} in team with ID ${teamId}:`,
			error,
		);
		throw error;
	}
}

function arrayBufferToBase64(buffer: ArrayBuffer) {
	const bytes = new Uint8Array(buffer);
	let binary = "";

	for (let i = 0; i < bytes.byteLength; i += 1) {
		binary += String.fromCharCode(bytes[i]);
	}

	return window.btoa(binary);
}

export async function getTeamPhoto(teamId: string): Promise<string | null> {
	try {
		const response: AxiosResponse<ArrayBuffer> = await graphApi.get(
			`/teams/${teamId}/photo/$value`,
			{
				responseType: "arraybuffer",
			},
		);
		const contentType = response.headers["content-type"] ?? "image/jpeg";

		return `data:${contentType};base64,${arrayBufferToBase64(response.data)}`;
	} catch (error) {
		if (
			error &&
			typeof error === "object" &&
			"response" in error &&
			(error as { response?: { status?: number } }).response?.status === 404
		) {
			return null;
		}

		console.error(`Error fetching photo for team with ID ${teamId}:`, error);
		throw error;
	}
}
